<template>
  <div class="notifications">
    <b-overlay
      :show="loading"
      variant="transparent"
      rounded="sm"
    >
      <template #overlay>
        <loading-spinner />
      </template>

      <div class="nt-card">
        <div
          class="nt-card-glow"
          aria-hidden="true"
        />
        <div class="nt-head">
          <div class="nt-head-title">
            <span class="nt-icon">
              <feather-icon
                icon="BellIcon"
                size="20"
              />
            </span>
            <div>
              <h3 class="nt-title">
                {{ $t('Notification Settings') }}
              </h3>
              <p>{{ $t('Choose how you want to be notified.') }}</p>
            </div>
          </div>
          <button
            v-if="notifications"
            type="button"
            class="nt-save"
            :disabled="loading"
            @click="update"
          >
            <feather-icon
              icon="CheckIcon"
              size="16"
            />
            {{ $t('Save') }}
          </button>
        </div>

        <div
          v-if="notifications"
          class="nt-list"
        >
          <div
            v-for="item in items"
            :key="item.key"
            class="nt-item"
            :class="{ on: notifications[item.key] }"
          >
            <span
              class="nt-item-icon"
              :class="item.color"
            >
              <feather-icon
                :icon="item.icon"
                size="20"
              />
            </span>
            <span class="nt-item-text">
              <strong>{{ $t(item.title) }}</strong>
              <small>{{ $t(item.description) }}</small>
            </span>
            <b-form-checkbox
              v-model="notifications[item.key]"
              class="nt-switch"
              name="check-button"
              switch
            />
          </div>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { BFormCheckbox, BOverlay } from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import LoadingSpinner from '@/components/LoadingSpinner'
import { gtmTrackNewEvent, showNotify, throwDefaultError } from '@/helpers/helpers'

export default {
  name: 'settings.notifications',
  components: {
    BFormCheckbox,
    BOverlay,

    LoadingSpinner,
  },
  data() {
    return {
      items: [
        {
          key: 'pushNotifications',
          title: 'Push Notifications',
          description: 'Instant notifications for messages and calls on your device.',
          icon: 'SmartphoneIcon',
          color: 'purple',
        },
        {
          key: 'smsNotifications',
          title: 'SMS Notifications',
          description: 'Important updates sent to your phone number.',
          icon: 'MessageSquareIcon',
          color: 'pink',
        },
        {
          key: 'emailNotifications',
          title: 'Email Notifications',
          description: 'News, campaigns and account updates in your inbox.',
          icon: 'MailIcon',
          color: 'blue',
        },
      ],
      loading: false,
      notifications: null,
    }
  },
  created() {
    this.getNotifications()
  },
  methods: {
    getNotifications() {
      this.loading = true
      useJwt.getSettings()
        .then(response => {
          this.notifications = response.data.body
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => { this.loading = false })
    },
    update() {
      const data = {
        pushNotifications: this.notifications.pushNotifications,
        smsNotifications: this.notifications.smsNotifications,
        emailNotifications: this.notifications.emailNotifications,
      }
      this.loading = true
      useJwt.updateSettings(data)
        .then(() => {
          showNotify(this.$toast, this.$t('Update successful'), 'success')

          gtmTrackNewEvent('savenotificationsettings', {
            category: 'Hesap',
            action: 'SaveNotificationSettings',
          })
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => { this.loading = false })
    },
  },
}
</script>

<style lang="scss" scoped>
.notifications {
  margin: 0 -25px;
}

.nt-card {
  position: relative;
  overflow: hidden;
  background: rgba(32, 22, 72, 0.72);
  border: 1px solid rgba(150, 110, 255, 0.28);
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(6, 2, 24, 0.45);
  padding: 26px 30px 30px;
  min-height: 200px;
}

.nt-card-glow {
  position: absolute;
  top: -60px;
  right: -60px;
  width: 280px;
  height: 280px;
  background: radial-gradient(circle, rgba(234, 3, 178, 0.25), transparent 70%);
  pointer-events: none;
}

.nt-head {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding-bottom: 18px;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(150, 110, 255, 0.2);
}

.nt-head-title {
  display: flex;
  align-items: center;
  gap: 14px;

  p {
    color: rgba(226, 220, 255, 0.72);
    font-size: 13px;
    margin: 0;
  }
}

.nt-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #8b5cf6, #a855f7);
  flex-shrink: 0;
}

.nt-title {
  color: #fff !important;
  font-size: 20px !important;
  font-weight: 700;
  margin: 0 0 2px;
}

.nt-save {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 9999px;
  padding: 10px 22px;
  font-weight: 600;
  font-size: 14px;
  color: #fff;
  background: linear-gradient(135deg, #8b5cf6, #a855f7);
  box-shadow: 0 6px 18px rgba(139, 92, 246, 0.35);
  cursor: pointer;

  &:hover:not(:disabled) { filter: brightness(1.1); }
  &:disabled { opacity: .6; cursor: not-allowed; }
}

.nt-list {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 760px;
}

.nt-item {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 0;
  padding: 16px 18px;
  border-radius: 16px;
  background: rgba(14, 10, 40, 0.45);
  border: 1px solid rgba(150, 110, 255, 0.2);
  transition: background .2s ease, border-color .2s ease, box-shadow .2s ease;

  &:hover {
    background: rgba(139, 92, 246, 0.1);
  }

  /* acik olan satir menubardaki secili sekme gibi */
  &.on {
    background: linear-gradient(135deg, rgba(234, 3, 178, 0.16) 0%, rgba(168, 85, 247, 0.14) 50%, rgba(99, 102, 241, 0.08) 100%);
    border-color: rgba(234, 3, 178, 0.32);
    box-shadow: 0 2px 14px rgba(234, 3, 178, 0.12);
  }
}

.nt-item-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;

  &.purple { background: linear-gradient(135deg, #7c3aed, #a855f7); }
  &.pink { background: linear-gradient(135deg, #ff4fa8, #c026d3); }
  &.blue { background: linear-gradient(135deg, #3b82f6, #6366f1); }
}

.nt-item-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    color: #fff;
    font-size: 15px;
    font-weight: 600;
  }

  small {
    color: rgba(226, 220, 255, 0.7);
    font-size: 13px;
    line-height: 18px;
  }
}

.nt-switch {
  flex-shrink: 0;
  margin: 0;

  /* Sadece renkler; ölçü ve konumlar temanın varsayılanında kalır (kaymayı önler) */
  ::v-deep .custom-control-label {
    cursor: pointer;

    &::before {
      background-color: rgba(255, 255, 255, 0.14);
      border-color: rgba(170, 140, 255, 0.35);
      box-shadow: none;
    }
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-color: #c026d3;
    background-image: linear-gradient(135deg, #ea03b2, #a855f7);
    border-color: transparent;
    box-shadow: 0 0 12px rgba(234, 3, 178, 0.4);
  }

  ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
    box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.25);
  }
}

@media (max-width: 575px) {
  .nt-card { padding: 20px 16px; }
  .nt-item { padding: 14px; gap: 12px; }
  .nt-save { width: 100%; justify-content: center; }
}
</style>
